import React from 'react';
import * as yup from 'yup';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useFormValidate } from '../../../hooks/formValidate';
import { PushNotificationTarget } from '../../../types/notification';
import { Segment } from '../../../types/segment';
import { useStringsContext } from '../../../contexts/strings';
import SegmentTargetSelect, { getSelectedTarget, getTargetId } from './segment-target-select';
import BaseDialog from '../base-dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Box, Stack, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import DatetimeField from '../fields/datetime-field';
import TextInputWithValidate from '../input/text-input-with-validate';

export type PushNotificationFormPayload = {
  scheduledTime: string;
  target: PushNotificationTarget;
  title: string;
  description: string;
};
export default function PushNotificationPopup(props: {
  abbrv: string;
  isNews?: boolean;
  topicContentType?: Segment['contentType'];
  segments: Segment[];
  content: {
    title?: string;
    description?: string;
    scheduledTime?: string | null;
    target?: PushNotificationTarget;
  };
  pushNotificationIsSend?: boolean;
  onCreate: (payload: PushNotificationFormPayload) => Promise<any>;
  onUpdate: (payload: PushNotificationFormPayload) => Promise<any>;
  onDelete: () => Promise<any>;
  onClose: () => void;
  isPending: boolean;
  isNew: boolean;
  itemInfo?: React.ReactNode;
}) {
  const {
    // abbrv,
    itemInfo,
    segments,
    content,
    pushNotificationIsSend,
    onCreate,
    onUpdate,
    onDelete,
    onClose,
    isPending,
    isNew,
    isNews,
    topicContentType,
  } = props;
  const { addFormValue, addFormValidation, isValid, isDirty } = useFormValidate();
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const [hideValidationError, setHideValidationError] = React.useState<boolean>(true);
  const [title, setTitle] = React.useState(content.title || '');
  const [description, setDescription] = React.useState(content.description || '');
  const [scheduledTime, setScheduledTime] = React.useState(content.scheduledTime || '');
  const initialTargetId = getTargetId(content.target);
  const [targetId, setTargetId] = React.useState<string>(initialTargetId);
  const [target, setTarget] = React.useState<PushNotificationTarget>(
    getSelectedTarget(segments, targetId)
  );
  const popupTitle = !isNew
    ? strings.pushNotifications.editPushNotificationTitle
    : strings.pushNotifications.createPushNotificationTitle;

  return (
    <BaseDialog open title={popupTitle} onClose={onClose}>
      <DialogContent>
        <Box sx={{ pt: 7, pb: 2 }}>
          {itemInfo ? <Box sx={{ mb: 8 }}>{itemInfo}</Box> : null}
          <Box sx={{ mb: 4 }}>
            <TextInputWithValidate
              value={title}
              label={strings.pushNotifications.fieldNameTitle}
              name="title"
              onValue={(key, value) => {
                addFormValue(key, value);
                setTitle(value || '');
              }}
              onValidation={(key, isValid) => {
                addFormValidation(key, isValid);
              }}
              validation={yup
                .string()
                .max(65, strings.validations.validPushNotificationTitleMaxLength)
                .required(strings.validations.isRequired)}
              hideValidationError={hideValidationError}
              fullWidth
            />
          </Box>
          <Box sx={{ mb: 4 }}>
            <TextInputWithValidate
              value={description}
              label={strings.pushNotifications.fieldNameMessage}
              name="description"
              onValue={(key, value) => {
                addFormValue(key, value);
                setDescription(value || '');
              }}
              onValidation={(key, isValid) => {
                addFormValidation(key, isValid);
              }}
              validation={yup
                .string()
                .max(150, strings.validations.validPushNotificationDescriptionMaxLength)
                .required(strings.validations.isRequired)}
              hideValidationError={hideValidationError}
              multiline
              rows={5}
              fullWidth
            />
          </Box>
          <Box sx={{ mb: 4 }}>
            <DatetimeField
              field={{
                title: strings.pushNotifications.pushNotificationField,
                id: 'scheduledTime',
                key: 'scheduledTime',
                value: scheduledTime,
                validation: yup
                  .date()
                  .required(strings.validations.isRequired)
                  .min(
                    moment().subtract(2, 'minutes').toDate(),
                    strings.validations.validDateInPast
                  )
                  .typeError(strings.validations.validDateTime),
              }}
              setChange={(
                fieldKey: string,
                value: string,
                fieldValidation: any,
                isTeamDataValue: boolean
              ) => {
                setScheduledTime(value);
                addFormValue('scheduledTime', value);
              }}
              setValidation={async (key, value, validate) => {
                const isValid = await validate.isValid(value);
                addFormValidation(key, isValid);
              }}
              hideValidationError={hideValidationError}
              fullWidth
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <SegmentTargetSelect
              value={targetId}
              isNews={isNews}
              topicContentType={topicContentType}
              segments={segments}
              name="target"
              label={strings.pushNotifications.segmentsSource}
              onValue={(key, value, segments) => {
                setTargetId(value || '');
                addFormValue(key, value);
                setTarget(getSelectedTarget(segments, value || ''));
              }}
              onValidation={(key, isValid) => {
                addFormValidation(key, isValid);
              }}
              hideValidationError={hideValidationError}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={(theme) => ({ borderTop: `1px solid ${theme.palette.grey[300]}` })}>
        <Stack direction="row" gap={2} sx={{ flex: 1 }}>
          {content.scheduledTime && !pushNotificationIsSend && !isNew ? (
            <LoadingButton
              color="error"
              variant="contained"
              onClick={async () => {
                await onDelete();
                setScheduledTime('');
                setTargetId('');
                onClose();
              }}
              loading={isPending}
            >
              {strings.pushNotifications.pushNotificationsDelete}
            </LoadingButton>
          ) : null}
        </Stack>
        <Stack direction="row" gap={2}>
          <Button
            color="primary"
            onClick={() => {
              onClose();
            }}
          >
            {strings.actions.cancel}
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!isDirty || !isValid) {
                setHideValidationError(false);
                return;
              }
              try {
                const payload = {
                  scheduledTime: moment(scheduledTime).format(),
                  target,
                  title,
                  description,
                };
                if (isNew) {
                  await onCreate(payload);
                } else {
                  await onUpdate(payload);
                }

                onClose();
                const message = strings.messages.pushNotificationSuccess;
                setTimeout(() => {
                  enqueueSnackbar(message, {
                    variant: 'success',
                  });
                  onClose();
                }, 200);
              } catch (error: any) {
                console.error(error);

                enqueueSnackbar(strings.messages.saveError, {
                  variant: 'error',
                });
              }
            }}
            disabled={!isDirty}
            loading={isPending}
          >
            {isNew
              ? strings.pushNotifications.pushNotificationCreateButton
              : strings.pushNotifications.pushNotificationSaveButton}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
}
